import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Divider,
  Flex,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { XCircleIcon } from '@heroicons/react/solid';
import { useState } from 'react';
import { useTranslations } from 'use-intl';

import {
  useGetSubscriptionQuery,
  useValidateMifidSectionMutation,
} from '@blockpulse3/graphql/hooks';
import {
  ResponsiveModal,
  ResponsiveModalFooter,
  ResponsiveModalProps,
} from '@blockpulse3/ui/commons';

import { MifidForm } from './MifidForm';
import { MifidSteps } from './MifidSteps';
import { MifidModalContextProvider } from './provider';
import { MifidQuestionForm } from './types';

type Props = {
  onClose: () => void;
  isReading?: boolean;
  subscriptionId: string;
} & Omit<ResponsiveModalProps, 'children'>;

export function MifidModal({
  onClose,
  isReading = false,
  subscriptionId = '',
  ...props
}: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nMifidAlert = useTranslations('MifidAlert');
  const i18nMifidStep = useTranslations('MifidStep');

  const [sectionIndex, setSectionIndex] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [isNextEnabled, setNextEnabled] = useState(false);
  const [tmpSectionData, setTmpSectionData] = useState<MifidQuestionForm>({});

  const [validateMifidSection, { loading: validateSectionLoading }] =
    useValidateMifidSectionMutation();

  const {
    data,
    refetch,
    loading: subscriptionLoading,
  } = useGetSubscriptionQuery({
    variables: { subscriptionId },
    // Make loading value update accordingly when refetching
    notifyOnNetworkStatusChange: true,
  });

  const mifidSections = data?.subscription.data.mergedMifidSections || [];
  const currentSection = mifidSections[sectionIndex];

  const isLastSection = sectionIndex === mifidSections.length - 1;
  const isFirstSection = sectionIndex === 0;

  const handleFormSubmit = (sectionData: MifidQuestionForm): void => {
    if (isReading) {
      if (isLastSection) {
        setSectionIndex(0);
        onClose();
      } else {
        setSectionIndex(sectionIndex + 1);
      }
      return;
    }

    // Prevent form submission if no data has been changed
    if (JSON.stringify(tmpSectionData) === JSON.stringify(sectionData)) {
      return;
    }
    setTmpSectionData(sectionData);

    // Prevent sending form if all answers have not been filled
    const inputCount = Object.values(sectionData).filter((value) => value);
    const sectionKeys = Object.keys(currentSection.questions);
    if (inputCount.length < sectionKeys.length) {
      return;
    }

    validateMifidSection({
      variables: {
        validateMifidSectionInput: {
          subscriptionId,
          mifidSectionId: currentSection.sectionId,
          answersInput: sectionData,
        },
      },
      onCompleted: async ({ validateMifidSection }) => {
        await refetch();
        if (!validateMifidSection) {
          setShowAlert(true);
          return;
        } else {
          setShowAlert(false);
          setTmpSectionData({});
          setSectionIndex(sectionIndex + 1);
        }

        // Close modal once all sections have been completed successfully
        if (isLastSection) {
          onClose();
        }
      },
    });
  };

  const handleCancel = (): void => {
    if (isFirstSection) {
      onClose();
    } else {
      setSectionIndex(sectionIndex - 1);
    }
  };

  const handleNextEnabled = (enabled: boolean): void => {
    setNextEnabled(enabled);
  };

  const isLoading = validateSectionLoading || subscriptionLoading;

  return (
    <MifidModalContextProvider
      isSectionDisabled={isLoading || isReading}
      subscriptionId={subscriptionId}
    >
      <ResponsiveModal isCentered scrollBehavior="inside" size="5xl" onClose={onClose} {...props}>
        <ModalOverlay />
        <ModalContent overflow="hidden">
          <ModalHeader>
            {i18nMifidStep('Title')}
            <Text fontSize="sm" fontWeight="500">
              {i18nMifidStep('Subtitle')}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            as={Stack}
            direction={{ base: 'column', md: 'row' }}
            p="0"
            position="relative"
            spacing="0"
          >
            <Stack
              bg="gray.50"
              boxShadow="sm"
              direction="column"
              flexShrink="0"
              flexWrap="nowrap"
              justifyContent="space-between"
              overflowX={{ base: 'auto', md: 'hidden' }}
              p="2"
              position={{ base: 'relative', md: 'sticky' }}
              top="0"
              width={{ base: 'full', md: '33%' }}
            >
              <MifidSteps mifidSections={mifidSections} sectionIndex={sectionIndex} />
              {showAlert && (
                <Stack p="2">
                  <Alert status="error">
                    <AlertIcon as={XCircleIcon} />
                    <Stack spacing="1">
                      <AlertTitle>{i18nMifidAlert('Title')}</AlertTitle>
                      <AlertDescription>{i18nMifidAlert('Subtitle')}</AlertDescription>
                    </Stack>
                  </Alert>
                </Stack>
              )}
            </Stack>
            <Flex
              direction="column"
              height="full"
              justify="space-between"
              overflowY={{ base: 'auto', md: 'hidden' }}
              px="5"
              width="full"
            >
              <MifidForm
                section={currentSection}
                onSubmit={handleFormSubmit}
                onUpdateNextEnabled={handleNextEnabled}
              />
            </Flex>
          </ModalBody>
          <Divider />
          <ResponsiveModalFooter>
            <Button type="button" variant="secondary" onClick={handleCancel}>
              {isFirstSection ? i18nGeneral('Cancel') : i18nGeneral('Back')}
            </Button>
            {isReading ? (
              <Button form="mifid" type="submit">
                {isLastSection ? i18nGeneral('Close') : i18nGeneral('Next')}
              </Button>
            ) : (
              <Button form="mifid" isDisabled={!isNextEnabled} isLoading={isLoading} type="submit">
                {isLastSection ? i18nGeneral('Submit') : i18nGeneral('Next')}
              </Button>
            )}
          </ResponsiveModalFooter>
        </ModalContent>
      </ResponsiveModal>
    </MifidModalContextProvider>
  );
}
